/**
 * Find Your Botanical Routine — quiz engine.
 *
 * Deterministic only: every completed answer path resolves to one of the
 * approved result-profile IDs in quiz-routine-data.json, and only the
 * stored copy for that profile/version is ever displayed. This file
 * contains no free-text generation of recommendation copy.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'spierbQuizFindYourRoutine:v1';

  var SAFETY_OPTIONS = [
    { value: 'hair_loss', label: 'Sudden or severe hair loss' },
    { value: 'persistent_irritation', label: 'Persistent scalp or skin irritation' },
    { value: 'broken_inflamed', label: 'Broken or inflamed skin/scalp' },
    { value: 'allergies', label: 'Known allergies to botanical ingredients' },
    { value: 'pregnancy', label: 'Pregnant or breastfeeding' },
    { value: 'none', label: 'None of these' }
  ];

  var PRODUCT_COUNT_OPTIONS = [
    { value: 'one', label: 'Just one product to start' },
    { value: 'two', label: 'Two products' },
    { value: 'complete', label: 'A complete routine' }
  ];

  var BRANCH_QUESTION = {
    id: 'branch',
    text: 'What would you like help with today?',
    options: [
      { value: 'hair_care', label: 'Hair care' },
      { value: 'hair_colour', label: 'Natural hair colour' },
      { value: 'scalp_care', label: 'Scalp care' },
      { value: 'skin_body', label: 'Skin & body care' }
    ]
  };

  var BRANCHES = {
    hair_care: {
      questions: [
        {
          id: 'goal',
          text: 'What is your main hair-care goal?',
          options: [
            { value: 'hair_fall', label: 'Hair fall or breakage' },
            { value: 'dry_frizz', label: 'Dryness or frizz' },
            { value: 'dull_shine', label: 'Dull hair — want more shine' },
            { value: 'weak_damage', label: 'Hair looks or feels weak / damaged' },
            { value: 'curl_definition', label: 'Curl definition & manageability' },
            { value: 'gentle_cleanse', label: 'A gentler cleansing routine' }
          ]
        },
        {
          id: 'scalp',
          text: 'How would you describe your scalp?',
          options: [
            { value: 'balanced', label: 'Balanced' },
            { value: 'dry', label: 'Dry' },
            { value: 'sensitive', label: 'Sensitive' },
            { value: 'oily', label: 'Oily' },
            { value: 'flaky', label: 'Flaky' }
          ],
          show: function (a) { return a.goal !== 'curl_definition'; }
        },
        {
          id: 'texture',
          text: 'What best describes your hair texture?',
          options: [
            { value: 'straight', label: 'Straight' },
            { value: 'wavy', label: 'Wavy' },
            { value: 'curly', label: 'Curly' },
            { value: 'coily', label: 'Coily' },
            { value: 'unsure', label: 'Not sure' }
          ],
          show: function (a) { return a.goal === 'dry_frizz' || a.goal === 'dull_shine'; }
        },
        {
          id: 'treatment',
          text: 'Has your hair had any chemical treatment?',
          options: [
            { value: 'untreated', label: 'No, untreated' },
            { value: 'chemical_colour', label: 'Chemically coloured' },
            { value: 'bleached', label: 'Bleached' },
            { value: 'chemically_treated', label: 'Relaxed, permed or keratin-treated' }
          ],
          show: function (a) { return a.goal === 'dry_frizz' || a.goal === 'weak_damage'; }
        }
      ],
      resolve: function (a) {
        switch (a.goal) {
          case 'hair_fall':
            if (a.scalp === 'dry' || a.scalp === 'sensitive') return 'HC02';
            if (a.scalp === 'oily' || a.scalp === 'flaky') return 'HC03';
            return 'HC01';
          case 'dry_frizz':
            if (a.treatment === 'chemical_colour' || a.treatment === 'bleached' || a.treatment === 'chemically_treated') return 'HC06';
            if (a.texture === 'curly' || a.texture === 'coily') return 'HC05';
            return 'HC04';
          case 'dull_shine':
            if (a.scalp === 'dry' || a.scalp === 'sensitive' || a.texture === 'curly' || a.texture === 'coily') return 'HC08';
            return 'HC07';
          case 'weak_damage':
            if (a.treatment === 'chemical_colour' || a.treatment === 'bleached' || a.treatment === 'chemically_treated') return 'HC10';
            return 'HC09';
          case 'curl_definition':
            return 'HC11';
          case 'gentle_cleanse':
            if (a.scalp === 'oily' || a.scalp === 'flaky') return 'HC13';
            if (a.scalp === 'dry' || a.scalp === 'sensitive') return 'HC14';
            return 'HC12';
        }
        return null;
      }
    },

    hair_colour: {
      questions: [
        {
          id: 'goal',
          text: 'What natural colour result are you hoping for?',
          options: [
            { value: 'copper', label: 'Copper / warm red' },
            { value: 'auburn', label: 'Auburn / deeper red' },
            { value: 'brown', label: 'Brown' },
            { value: 'dark_brown', label: 'Dark brown' },
            { value: 'black', label: 'Black' },
            { value: 'grey_coverage', label: 'Grey coverage / blending' },
            { value: 'colourless_conditioning', label: 'Conditioning only — no colour' }
          ]
        },
        {
          id: 'grey_target',
          text: 'Which tone would you like your grey coverage to blend towards?',
          options: [
            { value: 'warm_red', label: 'Warm red' },
            { value: 'brown', label: 'Brown' },
            { value: 'dark_brown', label: 'Dark brown' },
            { value: 'black', label: 'Black' }
          ],
          show: function (a) { return a.goal === 'grey_coverage'; }
        },
        {
          id: 'treatment',
          text: 'Has your hair been chemically coloured, bleached or treated before — or are you unsure of its history?',
          options: [
            { value: 'untreated', label: 'No, untreated' },
            { value: 'chemical_or_uncertain', label: 'Yes, or I’m not sure' }
          ]
        },
        {
          id: 'strand_test_willing',
          text: 'Are you willing to complete a strand test before a full application?',
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No, just show me the recommendation' }
          ]
        }
      ],
      resolve: function (a) {
        switch (a.goal) {
          case 'copper': return 'C01';
          case 'auburn': return 'C02';
          case 'brown': return 'C03';
          case 'dark_brown': return 'C04';
          case 'black': return 'C05';
          case 'grey_coverage':
            if (a.grey_target === 'warm_red') return 'C06';
            if (a.grey_target === 'brown' || a.grey_target === 'dark_brown') return 'C07';
            if (a.grey_target === 'black') return 'C08';
            return null;
          case 'colourless_conditioning': return 'C09';
        }
        return null;
      }
    },

    scalp_care: {
      questions: [
        {
          id: 'goal',
          text: 'What is your main scalp-care concern?',
          options: [
            { value: 'oily_buildup', label: 'Oil or product buildup' },
            { value: 'mild_flakes', label: 'Mild flakes' },
            { value: 'dry_tight', label: 'Dryness or tightness' },
            { value: 'sensitive_scalp', label: 'Scalp sensitivity' },
            { value: 'scalp_clarifying', label: 'Occasional deep cleansing' }
          ]
        },
        {
          id: 'sensitivity',
          text: 'How would you describe your scalp’s sensitivity?',
          options: [
            { value: 'not_sensitive', label: 'Not sensitive' },
            { value: 'unsure', label: 'Not sure' },
            { value: 'slightly_sensitive', label: 'Slightly sensitive' },
            { value: 'very_sensitive', label: 'Very sensitive' }
          ],
          show: function (a) { return a.goal !== 'sensitive_scalp'; }
        }
      ],
      resolve: function (a) {
        switch (a.goal) {
          case 'oily_buildup':
            return (a.sensitivity === 'slightly_sensitive' || a.sensitivity === 'very_sensitive') ? 'SC02' : 'SC01';
          case 'mild_flakes':
            return a.sensitivity === 'very_sensitive' ? 'SC05' : 'SC03';
          case 'dry_tight':
            return a.sensitivity === 'very_sensitive' ? 'SC05' : 'SC04';
          case 'sensitive_scalp':
            return 'SC05';
          case 'scalp_clarifying':
            return (a.sensitivity === 'slightly_sensitive' || a.sensitivity === 'very_sensitive') ? 'SC07' : 'SC06';
        }
        return null;
      }
    },

    skin_body: {
      questions: [
        {
          id: 'goal',
          text: 'What is your main skin & body care goal?',
          options: [
            { value: 'oily_skin', label: 'Oil absorption' },
            { value: 'gentle_skin_cleanse', label: 'Gentle cleansing' },
            { value: 'dry_sensitive_skin', label: 'Dry or sensitive skin' },
            { value: 'dull_skin', label: 'Dull-looking skin' },
            { value: 'face_exfoliation', label: 'Facial exfoliation' },
            { value: 'body_exfoliation', label: 'Body exfoliation' },
            { value: 'deep_skin_cleanse', label: 'Occasional deep cleansing' }
          ]
        },
        {
          id: 'sensitivity',
          text: 'How would you describe your skin’s sensitivity?',
          options: [
            { value: 'not_sensitive', label: 'Not sensitive' },
            { value: 'unsure', label: 'Not sure' },
            { value: 'slightly_sensitive', label: 'Slightly sensitive' },
            { value: 'very_sensitive', label: 'Very sensitive' }
          ],
          show: function (a) {
            return a.goal === 'oily_skin' || a.goal === 'dull_skin' || a.goal === 'face_exfoliation' || a.goal === 'deep_skin_cleanse';
          }
        },
        {
          id: 'area',
          text: 'Which area are you looking to exfoliate?',
          options: [
            { value: 'body', label: 'Body' },
            { value: 'hands_feet', label: 'Hands or feet' }
          ],
          show: function (a) { return a.goal === 'body_exfoliation'; }
        }
      ],
      resolve: function (a) {
        switch (a.goal) {
          case 'oily_skin':
            return (a.sensitivity === 'slightly_sensitive' || a.sensitivity === 'very_sensitive') ? 'SK02' : 'SK01';
          case 'gentle_skin_cleanse':
            return 'SK03';
          case 'dry_sensitive_skin':
            return 'SK04';
          case 'dull_skin':
            return (a.sensitivity === 'slightly_sensitive' || a.sensitivity === 'very_sensitive') ? 'SK06' : 'SK05';
          case 'face_exfoliation':
            return (a.sensitivity === 'slightly_sensitive' || a.sensitivity === 'very_sensitive') ? 'SK08' : 'SK07';
          case 'body_exfoliation':
            return a.area === 'hands_feet' ? 'SK10' : 'SK09';
          case 'deep_skin_cleanse':
            return (a.sensitivity === 'slightly_sensitive' || a.sensitivity === 'very_sensitive') ? 'SK12' : 'SK11';
        }
        return null;
      }
    }
  };

  var SAFETY_QUESTION = {
    id: 'safety',
    text: 'Do any of these apply to you? (select all that apply)',
    multi: true,
    options: SAFETY_OPTIONS
  };

  var PRODUCT_COUNT_QUESTION = {
    id: 'product_count',
    text: 'How many products would you like recommended?',
    options: PRODUCT_COUNT_OPTIONS
  };

  function findQuestion(id, answers) {
    if (id === 'branch') return BRANCH_QUESTION;
    if (id === 'safety') return SAFETY_QUESTION;
    if (id === 'product_count') return PRODUCT_COUNT_QUESTION;
    var branch = BRANCHES[answers.branch];
    if (!branch) return null;
    for (var i = 0; i < branch.questions.length; i++) {
      if (branch.questions[i].id === id) return branch.questions[i];
    }
    return null;
  }

  function getNextQuestion(answers) {
    if (answers.branch === undefined) return BRANCH_QUESTION;
    var branch = BRANCHES[answers.branch];
    if (!branch) return null;
    for (var i = 0; i < branch.questions.length; i++) {
      var q = branch.questions[i];
      if (answers[q.id] !== undefined) continue;
      if (q.show && !q.show(answers)) continue;
      return q;
    }
    if (answers.safety === undefined) return SAFETY_QUESTION;
    if (answers.product_count === undefined) return PRODUCT_COUNT_QUESTION;
    return null;
  }

  function estimateTotalSteps(answers) {
    var total = 1; // branch question
    if (answers.branch && BRANCHES[answers.branch]) {
      var branch = BRANCHES[answers.branch];
      for (var i = 0; i < branch.questions.length; i++) {
        var q = branch.questions[i];
        if (!q.show || q.show(answers)) total++;
      }
    }
    return total + 2; // safety + product_count
  }

  function buildWarnings(data, answers) {
    var warnings = [];
    var safety = answers.safety || [];
    var hasSafetyTrigger = safety.some(function (v) { return v !== 'none'; });
    if (hasSafetyTrigger) warnings.push(data.copy.professional_care_notice);

    if (answers.branch === 'hair_colour') {
      if (answers.treatment === 'chemical_or_uncertain') {
        warnings.push(data.copy.chemical_treatment_warning);
      }
      if (answers.strand_test_willing === 'no') {
        warnings.push(data.copy.strand_test_refusal_warning);
      }
    }
    return warnings;
  }

  function labelFor(question, value) {
    if (!question) return value;
    var found = question.options.filter(function (o) { return o.value === value; })[0];
    return found ? found.label : value;
  }

  function buildAnswerChips(answers, answerOrder) {
    return answerOrder.map(function (id) {
      var question = findQuestion(id, answers);
      var value = answers[id];
      var label;
      if (Array.isArray(value)) {
        label = value.map(function (v) { return labelFor(question, v); }).join(', ');
      } else {
        label = labelFor(question, value);
      }
      return { id: id, questionText: question ? question.text : id, label: label };
    });
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var currencySymbol = '$';

  function formatMoney(cents) {
    return currencySymbol + (cents / 100).toFixed(2);
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function saveState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* storage unavailable — quiz still works, just won't survive a refresh */ }
  }

  function clearState() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function initQuizRoot(root) {
    var answers = {};
    var answerOrder = [];
    var completed = false;
    var quizData = null;
    var productMap = {};

    var mapScript = root.querySelector('[data-quiz-product-map]');
    if (mapScript) {
      try { productMap = JSON.parse(mapScript.textContent); } catch (e) { productMap = {}; }
    }

    var startButton = root.querySelector('[data-quiz-start]');
    var quizCard = root.querySelector('[data-quiz-area="quiz"]');
    var progressEl = root.querySelector('[data-quiz-progress]');
    var questionStage = root.querySelector('[data-quiz-question-stage]');
    var resultBlocks = root.querySelectorAll('[data-quiz-results-block]');
    var productsBlock = root.querySelector('[data-quiz-products-block]');
    var placeholder = root.querySelector('[data-quiz-placeholder]');
    var answersList = root.querySelector('[data-quiz-answers-list]');
    var editButton = root.querySelector('[data-quiz-edit]');
    var retakeButton = root.querySelector('[data-quiz-retake]');
    var explanationEl = root.querySelector('[data-quiz-explanation]');
    var routineListEl = root.querySelector('[data-quiz-routine-list]');
    var guideListEl = root.querySelector('[data-quiz-guide-list]');
    var warningsEl = root.querySelector('[data-quiz-warnings]');
    var productCardsEl = root.querySelector('[data-quiz-product-cards]');
    var addRoutineButton = root.querySelector('[data-quiz-add-routine]');
    var profileBadgeEl = root.querySelector('[data-quiz-profile-badge]');
    var summarySubEl = root.querySelector('[data-quiz-summary-sub]');
    var summaryRowEl = root.querySelector('[data-quiz-summary-row]');

    function persist() {
      saveState({ answers: answers, answerOrder: answerOrder, completed: completed });
    }

    function restart() {
      answers = {};
      answerOrder = [];
      completed = false;
      clearState();
      root.setAttribute('data-quiz-started', 'true');
      render();
    }

    function truncateAt(questionId) {
      var idx = answerOrder.indexOf(questionId);
      if (idx === -1) return;
      var keep = answerOrder.slice(0, idx);
      var newAnswers = {};
      keep.forEach(function (id) { newAnswers[id] = answers[id]; });
      answers = newAnswers;
      answerOrder = keep;
      completed = false;
      persist();
      render();
    }

    function commitAnswer(question, value) {
      answers[question.id] = value;
      if (answerOrder.indexOf(question.id) === -1) answerOrder.push(question.id);
      var next = getNextQuestion(answers);
      if (!next) {
        completed = true;
      }
      persist();
      render();
    }

    function renderProgress() {
      if (!progressEl) return;
      if (completed) {
        progressEl.innerHTML = '';
        return;
      }
      var total = estimateTotalSteps(answers);
      var done = answerOrder.length;
      var pct = Math.min(100, Math.round((done / total) * 100));
      progressEl.innerHTML =
        '<div class="quiz-routine__progress-track"><div class="quiz-routine__progress-fill" style="width:' + pct + '%"></div></div>' +
        '<p class="quiz-routine__progress-label">Step ' + (done + 1) + ' of about ' + total + '</p>';
    }

    function renderQuestionStage() {
      if (!questionStage) return;
      var question = getNextQuestion(answers);
      if (!question) {
        questionStage.innerHTML = '';
        return;
      }
      var isMulti = !!question.multi;
      var selected = answers[question.id];
      if (isMulti && !Array.isArray(selected)) selected = [];

      var html = '<h3 class="quiz-routine__question-heading">' + escapeHtml(question.text) + '</h3>';
      html += '<div class="quiz-routine__options" role="group">';
      question.options.forEach(function (opt) {
        var pressed = isMulti ? selected.indexOf(opt.value) !== -1 : selected === opt.value;
        html += '<button type="button" class="quiz-routine__option" data-quiz-option="' + escapeHtml(opt.value) + '" aria-pressed="' + pressed + '">' + escapeHtml(opt.label) + '</button>';
      });
      html += '</div>';
      html += '<div class="quiz-routine__nav">';
      html += '<button type="button" class="quiz-routine__nav-button" data-quiz-back' + (answerOrder.length === 0 ? ' disabled' : '') + '>Back</button>';
      if (isMulti) {
        html += '<button type="button" class="quiz-routine__nav-button" data-quiz-multi-continue>Continue</button>';
      }
      html += '</div>';
      questionStage.innerHTML = html;

      questionStage.querySelectorAll('[data-quiz-option]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var value = btn.getAttribute('data-quiz-option');
          if (isMulti) {
            var current = Array.isArray(answers[question.id]) ? answers[question.id].slice() : [];
            if (value === 'none') {
              current = current.indexOf('none') !== -1 ? [] : ['none'];
            } else {
              current = current.filter(function (v) { return v !== 'none'; });
              var pos = current.indexOf(value);
              if (pos === -1) current.push(value); else current.splice(pos, 1);
            }
            answers[question.id] = current;
            persist();
            renderQuestionStage();
          } else {
            commitAnswer(question, value);
          }
        });
      });

      var backBtn = questionStage.querySelector('[data-quiz-back]');
      if (backBtn) {
        backBtn.addEventListener('click', function () {
          if (answerOrder.length === 0) return;
          var lastId = answerOrder[answerOrder.length - 1];
          truncateAt(lastId);
        });
      }

      var multiContinue = questionStage.querySelector('[data-quiz-multi-continue]');
      if (multiContinue) {
        multiContinue.addEventListener('click', function () {
          if (answerOrder.indexOf(question.id) === -1) answerOrder.push(question.id);
          if (answers[question.id] === undefined) answers[question.id] = [];
          if (!getNextQuestion(answers)) completed = true;
          persist();
          render();
        });
      }
    }

    function renderAnswersSummary() {
      if (!answersList) return;
      var chips = buildAnswerChips(answers, answerOrder);
      answersList.innerHTML = chips.map(function (chip) {
        return '<li class="quiz-routine__answer-chip" data-quiz-chip="' + escapeHtml(chip.id) + '" title="Click to edit" role="button" tabindex="0">' + escapeHtml(chip.label) + '</li>';
      }).join('');
      answersList.querySelectorAll('[data-quiz-chip]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          truncateAt(chip.getAttribute('data-quiz-chip'));
        });
        chip.addEventListener('keydown', function (evt) {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
            truncateAt(chip.getAttribute('data-quiz-chip'));
          }
        });
      });
    }

    function productCard(key, reason) {
      var info = productMap[key] || { assigned: false };
      var catalogueEntry = (quizData.catalogue && quizData.catalogue[key]) || { name: key };
      var name = info.assigned ? info.title : catalogueEntry.name;

      var media = '';
      if (info.assigned && info.image) {
        media = '<div class="quiz-routine__product-media"><img src="' + escapeHtml(info.image) + '" alt="' + escapeHtml(name) + '" loading="lazy"></div>';
      } else {
        media = '<div class="quiz-routine__product-media"></div>';
      }

      var bodyContent = '<div class="quiz-routine__product-title">' + escapeHtml(name) + '</div>';
      bodyContent += '<p class="quiz-routine__product-reason">' + escapeHtml(reason || '') + '</p>';

      if (!info.assigned) {
        var comingSoonActions = '<span class="quiz-routine__status-label">Coming soon</span>';
        return '<div class="quiz-routine__product-card" data-quiz-product-card data-key="' + escapeHtml(key) + '" data-purchasable="false">' +
          media +
          '<div class="quiz-routine__product-body">' + bodyContent + '</div>' +
          '<div class="quiz-routine__product-actions">' + comingSoonActions + '</div>' +
          '</div>';
      }

      bodyContent += '<div class="quiz-routine__product-price">' + formatMoney(info.price) + '</div>';

      var variants = info.variants || [];
      var availableVariants = variants.filter(function (v) { return v.available; });
      var isAvailable = !!info.available && availableVariants.length > 0;

      if (variants.length > 1) {
        bodyContent += '<select class="quiz-routine__product-variant" data-quiz-variant-select>';
        variants.forEach(function (v) {
          bodyContent += '<option value="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' + escapeHtml(v.title) + (v.available ? '' : ' (Sold out)') + '</option>';
        });
        bodyContent += '</select>';
      }

      var defaultVariantId = (variants.filter(function (v) { return v.available; })[0] || variants[0] || {}).id || '';

      var actionsContent = '';
      if (isAvailable) {
        actionsContent += '<quantity-input class="quantity">' +
          '<button class="quantity__button" name="minus" type="button"><span class="visually-hidden">Decrease quantity</span>&minus;</button>' +
          '<input class="quantity__input" type="number" name="quantity" value="1" min="1" step="1" data-quiz-qty-input aria-label="Quantity">' +
          '<button class="quantity__button" name="plus" type="button"><span class="visually-hidden">Increase quantity</span>+</button>' +
          '</quantity-input>';
        actionsContent += '<button type="button" class="quiz-routine__add-button" data-quiz-add-single data-variant-id="' + defaultVariantId + '">Add</button>';
      } else {
        actionsContent += '<button type="button" class="quiz-routine__add-button" disabled>Sold out</button>';
      }

      return '<div class="quiz-routine__product-card" data-quiz-product-card data-key="' + escapeHtml(key) + '" data-purchasable="' + isAvailable + '" data-variant-id="' + escapeHtml(defaultVariantId) + '" data-quantity="1" data-unit-price="' + (info.price || 0) + '">' +
        media +
        '<div class="quiz-routine__product-body">' + bodyContent + '</div>' +
        '<div class="quiz-routine__product-actions">' + actionsContent + '</div>' +
        '</div>';
    }

    function wireProductCard(cardEl) {
      var variantSelect = cardEl.querySelector('[data-quiz-variant-select]');
      if (variantSelect) {
        variantSelect.addEventListener('change', function () {
          cardEl.setAttribute('data-variant-id', variantSelect.value);
          var addBtn = cardEl.querySelector('[data-quiz-add-single]');
          if (addBtn) addBtn.setAttribute('data-variant-id', variantSelect.value);
        });
      }
      // The <quantity-input> custom element (global.js) already wires up its own
      // plus/minus buttons and dispatches `change` on the input — we only need to
      // mirror the resulting value onto the card so addToCart() can read it.
      var qtyInput = cardEl.querySelector('[data-quiz-qty-input]');
      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          var val = Math.max(1, parseInt(qtyInput.value, 10) || 1);
          cardEl.setAttribute('data-quantity', String(val));
          updateRoutineTotal();
        });
      }

      var addBtn = cardEl.querySelector('[data-quiz-add-single]');
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var variantId = cardEl.getAttribute('data-variant-id');
          var quantity = parseInt(cardEl.getAttribute('data-quantity'), 10) || 1;
          addToCart([{ id: variantId, quantity: quantity }], addBtn);
        });
      }
    }

    function refreshCartIconBubble() {
      var base = (window.routes && window.routes.cart_url) || '/cart';
      fetch(base + '?section_id=cart-icon-bubble')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newEl = doc.querySelector('#cart-icon-bubble');
          var oldEl = document.querySelector('#cart-icon-bubble');
          if (newEl && oldEl) oldEl.innerHTML = newEl.innerHTML;
        })
        .catch(function (e) { console.error(e); });
    }

    function showMultiItemAddedNotification(cardEls) {
      var notification = document.querySelector('cart-notification');
      if (!notification || typeof notification.open !== 'function') return;
      var productEl = document.getElementById('cart-notification-product');
      if (productEl) {
        productEl.innerHTML = cardEls.map(function (cardEl) {
          var img = cardEl.querySelector('.quiz-routine__product-media img');
          var titleEl = cardEl.querySelector('.quiz-routine__product-title');
          var name = titleEl ? titleEl.textContent : '';
          var imageHtml = img
            ? '<div class="cart-notification-product__image global-media-settings"><img src="' + img.getAttribute('src') + '" alt="" width="70" loading="lazy"></div>'
            : '';
          return '<div class="cart-item">' + imageHtml + '<div><h3 class="cart-notification-product__name h4">' + escapeHtml(name) + '</h3></div></div>';
        }).join('');
      }
      // cart-notification-button (View cart / Check out) is static markup, not per-item, so it
      // doesn't need re-rendering here the way the single-item path re-fetches it.
      if (notification.header && typeof notification.header.reveal === 'function') {
        notification.header.reveal();
      }
      notification.open();
    }

    function addToCart(items, triggerButton, onSuccess) {
      if (!items.length) return;
      if (triggerButton) { triggerButton.disabled = true; }
      // Single-item adds use the FLAT /cart/add.js body shape ({id, quantity, ...}), matching
      // what the theme's own product-form.js sends — that shape's response has a top-level
      // `key`, which cart-notification.js's renderContents() needs to find the newly-added
      // line item in the re-rendered section HTML. The multi-item {items:[...]} shape (needed
      // for "add complete routine") has no top-level key, so its response can't drive
      // renderContents() — for that case we only refresh the header's cart-icon-bubble count.
      var isSingleItem = items.length === 1;
      var cartComponent = isSingleItem
        ? (document.querySelector('cart-notification') || document.querySelector('cart-drawer'))
        : null;
      if (cartComponent && typeof cartComponent.setActiveElement === 'function') {
        cartComponent.setActiveElement(triggerButton || document.activeElement);
      }
      var body = isSingleItem ? { id: items[0].id, quantity: items[0].quantity } : { items: items };
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        body.sections = cartComponent.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }
      fetch(window.quizRoutineCartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            console.error(response.message || response.description);
            return response;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quiz-find-your-routine', cartData: response });
          }
          // cartComponent.renderContents() assumes the theme's own markup shape; if anything about
          // it throws (missing section, unexpected DOM), fall back to at least updating the cart
          // icon count rather than leaving the shopper with zero feedback that the add worked.
          if (cartComponent && typeof cartComponent.renderContents === 'function') {
            try {
              cartComponent.renderContents(response);
            } catch (renderError) {
              console.error('Cart notification failed to render, falling back to icon refresh:', renderError);
              refreshCartIconBubble();
            }
          } else {
            refreshCartIconBubble();
            if (typeof onSuccess === 'function') onSuccess();
          }
          return response;
        })
        .catch(function (e) { console.error(e); })
        .finally(function () {
          if (triggerButton) triggerButton.disabled = false;
        });
    }

    function updateRoutineTotal() {
      if (!addRoutineButton || addRoutineButton.hidden) return;
      var purchasableCards = Array.prototype.slice.call(productCardsEl.querySelectorAll('[data-purchasable="true"]'));
      var totalCents = purchasableCards.reduce(function (sum, cardEl) {
        var unitPrice = parseInt(cardEl.getAttribute('data-unit-price'), 10) || 0;
        var qty = parseInt(cardEl.getAttribute('data-quantity'), 10) || 1;
        return sum + unitPrice * qty;
      }, 0);
      addRoutineButton.textContent = 'Add complete routine to cart • ' + formatMoney(totalCents);
    }

    function renderProfileBadge(profile) {
      if (!profileBadgeEl) return;
      profileBadgeEl.innerHTML = '<span>' + escapeHtml(profile.title) + '</span>';
    }

    function renderSummaryStrip(profile, version) {
      if (!summaryRowEl) return;
      var versionData = profile.versions[version];
      var keys = versionData.products;
      var catalogue = quizData.catalogue || {};

      if (summarySubEl) {
        var count = keys.length;
        summarySubEl.textContent = count + (count === 1 ? ' key ingredient tailored for you.' : ' key ingredients tailored for you.');
      }

      summaryRowEl.innerHTML = keys.map(function (key) {
        var info = productMap[key] || { assigned: false };
        var entry = catalogue[key] || { name: key, role: '' };
        var name = info.assigned ? info.title : entry.name;
        var media = info.assigned && info.image
          ? '<img src="' + escapeHtml(info.image) + '" alt="" loading="lazy">'
          : '';
        return '<div class="quiz-routine__summary-item">' +
          '<div class="quiz-routine__summary-media">' + media + '</div>' +
          '<div class="quiz-routine__summary-name">' + escapeHtml(name) + '</div>' +
          '<p class="quiz-routine__summary-role">' + escapeHtml(entry.role || '') + '</p>' +
          '</div>';
      }).join('');
    }

    function renderProducts(profile, version) {
      if (!productCardsEl) return;
      var versionData = profile.versions[version];
      var reasons = quizData.product_reasons || {};
      productCardsEl.innerHTML = versionData.products.map(function (key) {
        return productCard(key, reasons[key]);
      }).join('');

      productCardsEl.querySelectorAll('[data-quiz-product-card]').forEach(wireProductCard);

      var purchasableCards = Array.prototype.slice.call(productCardsEl.querySelectorAll('[data-purchasable="true"]'));
      if (addRoutineButton) {
        if (purchasableCards.length >= 2) {
          addRoutineButton.hidden = false;
          addRoutineButton.onclick = function () {
            var items = purchasableCards.map(function (cardEl) {
              return {
                id: cardEl.getAttribute('data-variant-id'),
                quantity: parseInt(cardEl.getAttribute('data-quantity'), 10) || 1
              };
            });
            addToCart(items, addRoutineButton, function () {
              showMultiItemAddedNotification(purchasableCards);
            });
          };
          updateRoutineTotal();
        } else {
          addRoutineButton.hidden = true;
        }
      }
    }

    function renderResults() {
      var branchDef = BRANCHES[answers.branch];
      var profileId = branchDef ? branchDef.resolve(answers) : null;
      var profile = profileId ? quizData.profiles[profileId] : null;

      if (!profile) {
        if (explanationEl) explanationEl.innerHTML = '<p>We couldn’t match your answers to a stored result. Please retake the quiz.</p>';
        return;
      }

      var version = answers.product_count || 'complete';
      var versionData = profile.versions[version];

      renderProfileBadge(profile);
      renderSummaryStrip(profile, version);

      if (explanationEl) {
        explanationEl.innerHTML = versionData.explanation.map(function (p) {
          return '<p>' + escapeHtml(p) + '</p>';
        }).join('');
      }

      if (routineListEl) {
        routineListEl.innerHTML = versionData.routine.map(function (step) {
          return '<li>' + escapeHtml(step) + '</li>';
        }).join('');
      }

      if (guideListEl) {
        guideListEl.innerHTML = (profile.guides || []).map(function (title) {
          return '<li class="quiz-routine__guide-item">' + escapeHtml(title) + '</li>';
        }).join('');
      }

      if (warningsEl) {
        var warnings = buildWarnings(quizData, answers);
        if (profile.additional_caution) warnings.push(profile.additional_caution);
        warningsEl.innerHTML = warnings.map(function (w) {
          return '<div class="quiz-routine__warning">' + escapeHtml(w) + '</div>';
        }).join('');
      }

      renderProducts(profile, version);
    }

    function render() {
      renderProgress();
      root.setAttribute('data-quiz-completed', completed ? 'true' : 'false');

      if (completed) {
        if (questionStage) questionStage.innerHTML = '';
        if (quizCard) quizCard.hidden = true;
        Array.prototype.forEach.call(resultBlocks, function (el) { el.hidden = false; });
        if (productsBlock) productsBlock.hidden = false;
        if (placeholder) placeholder.hidden = true;
        renderAnswersSummary();
        renderResults();
      } else {
        if (quizCard) quizCard.hidden = false;
        Array.prototype.forEach.call(resultBlocks, function (el) { el.hidden = true; });
        if (productsBlock) productsBlock.hidden = true;
        if (placeholder) placeholder.hidden = false;
        renderQuestionStage();
      }
    }

    if (startButton) {
      startButton.addEventListener('click', function () {
        root.setAttribute('data-quiz-started', 'true');
        render();
      });
    }
    if (editButton) {
      editButton.addEventListener('click', function () {
        if (answerOrder.length) truncateAt(answerOrder[0]);
      });
    }
    if (retakeButton) {
      retakeButton.addEventListener('click', restart);
    }

    function boot() {
      fetch(window.quizRoutineDataUrl)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          quizData = data;
          var saved = loadState();
          if (saved && saved.answerOrder && saved.answerOrder.length) {
            answers = saved.answers || {};
            answerOrder = saved.answerOrder || [];
            completed = !!saved.completed;
            root.setAttribute('data-quiz-started', 'true');
          }
          render();
        })
        .catch(function (e) { console.error('Quiz data failed to load', e); });
    }

    boot();
  }

  function initAll() {
    document.querySelectorAll('[data-quiz-root]').forEach(function (root) {
      if (root.dataset.quizCurrencySymbol) currencySymbol = root.dataset.quizCurrencySymbol;
      if (root.getAttribute('data-quiz-initialized') === 'true') return;
      root.setAttribute('data-quiz-initialized', 'true');
      initQuizRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector('[data-quiz-root]');
    if (root && root.getAttribute('data-quiz-initialized') !== 'true') {
      root.setAttribute('data-quiz-initialized', 'true');
      initQuizRoot(root);
    }
  });
}());
